import { useEffect, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Research from "@/pages/Research";
import Blog from "@/pages/Blog";
import Writing from "@/pages/Writing";
import Contact from "@/pages/Contact";
import Admin from "@/pages/Admin";
import AtlasEntry from "@/pages/AtlasEntry";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-white px-6 text-center">
          <div>
            <p className="overline-mono">Something broke</p>
            <p className="font-serif text-2xl text-navy">This section failed to load. Please refresh the page.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function SiteChrome({ children }) {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <div className="min-h-screen bg-white text-navy antialiased">
      {!isAdmin && <Header />}
      {children}
      {!isAdmin && <Footer />}
    </div>
  );
}

const NotFound = () => (
  <div className="wrap flex min-h-[70vh] flex-col justify-center py-32">
    <p className="overline-mono">404 — Not found</p>
    <h1 className="mb-8 font-serif text-4xl text-paper sm:text-6xl">This page has been shelved.</h1>
    <Link to="/" className="btn-ghost w-fit">
      Return home
    </Link>
  </div>
);

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollManager />
        <SiteChrome>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/research" element={<Research />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/writing/:slug" element={<Writing />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/atlas/:t/:s/:a" element={<AtlasEntry />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </SiteChrome>
        <Toaster theme="light" position="bottom-right" />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;